import { ArrayUtils } from '@app/shared/utils/array-utils';

export class ObjectUtils {
  /**
   * Checks recursively whether the given object contains any values besides
   * null, [], or {}
   * @param o a primitive, Object, or Array
   * @returns boolean answering whether the object is empty
   */
  static getIsObjectEmpty(o: string | number | Array<any> | any) {
    if ((typeof o === 'string' && o !== '') || typeof o === 'number') return false;

    // null case
    if (o == null) return true;

    // empty string
    else if (o === '') return true;

    // []
    else if (
        Object.prototype.toString.call(o) === '[object Array]' &&
        o?.length === 0
      ) return true;

    // [{}, {}]
    else if (
      Object.prototype.toString.call(o) === '[object Array]' &&
      o.every(v => this.getIsObjectEmpty(v))
    ) return true;

    // {}
    else if (Object.keys(o)?.length === 0) return true;

    // {a: {b: {} } }
    else if (
        Object.keys(o)?.length > 0 &&
        Object.keys(o).every(key => this.getIsObjectEmpty(o[key]))
      ) return true;

    return false;
  }

  static objectsAreEqualRecursive = <T> (a: T, b: T) => {
    if (typeof a !== typeof b) {
      return false;
    }

    // Handle base cases for primitive types and null/undefined
    if (a === b) {
      return true;
    }

    if (a == null || b == null) {
      return false;
    }

    // Check if both objects are arrays
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) {
        return false;
      }

      for (let i = 0; i < a.length; i++) {
        if (!this.objectsAreEqualRecursive(a[i], b[i])) {
          return false;
        }
      }

      return true;
    }

    // Check if both objects are objects (non-array objects)
    if (typeof a === 'object' && typeof b === 'object') {
      const keys1 = Object.keys(a);
      const keys2 = Object.keys(b);

      if (keys1.length !== keys2.length) {
        return false;
      }

      for (const key of keys1) {
        if (!this.objectsAreEqualRecursive(a[key], b[key])) {
          return false;
        }
      }

      return true;
    }

    // If none of the above conditions are met, the objects are not equal
    return false;
  };
}

export const enumerablePropertiesAreTheSame = <T> (a: T, b: T) => {
  if (a == null || b == null)
    return a === b;

  const objAKeys = Object.keys(a);
  const objBKeys = Object.keys(b);

  if (ArrayUtils.aMissingElementFromB(objAKeys, objBKeys) || ArrayUtils.aMissingElementFromB(objBKeys, objAKeys))
    return false;

  for (const key of objAKeys) {
    if (a[key] !== b[key])
      return false;
  }

  return true;
};
